/**
 * The overlay staff watch while a card is being tapped on the reader.
 *
 * Its Cancel is the escape action every forced UI needs: it stops the reader
 * and releases whatever was held (a pending POS order), and if the tap
 * already went through, the charge is recorded and reported as such.
 */
import React from "react";
import { CreditCard, X } from "lucide-react";
import { ReaderWaitState } from "../../../hooks/useReaderWait";
import { useTheme } from "../../../context/ThemeContext";
import { getCardBackground, getLightBorder, getPrimaryText, getSecondaryText } from "../../../utils/themeUtils";

interface ReaderWaitPanelProps {
  wait: ReaderWaitState;
  onCancel: () => void;
  /** Test mode only — rendered when the reader is simulated. */
  onSimulateTap?: () => void;
  formatAmount?: (n: number) => string;
}

const ReaderWaitPanel: React.FC<ReaderWaitPanelProps> = ({ wait, onCancel, onSimulateTap, formatAmount }) => {
  const { isDarkMode } = useTheme();
  const amountLine =
    typeof wait.amount === "number"
      ? formatAmount
        ? formatAmount(wait.amount)
        : `$${wait.amount.toFixed(2)}`
      : null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Waiting for the card reader"
      data-testid="reader-wait-panel"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-60"
    >
      <div className={`${getCardBackground(isDarkMode)} rounded-lg shadow-xl w-full max-w-sm border ${getLightBorder(isDarkMode)}`}>
        <div className="p-6 text-center">
          <div className="mx-auto mb-3 h-14 w-14 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
            <CreditCard className="text-gray-500 dark:text-gray-400" size={28} />
          </div>
          <h3 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>
            {amountLine ? `Tap or insert card — ${amountLine}` : "Tap or insert card"}
          </h3>
          <p className={`mt-1 text-sm ${getSecondaryText(isDarkMode)}`}>
            {wait.readerLabel ? `On ${wait.readerLabel}. ` : ""}
            {wait.message || "Waiting for the card…"}
          </p>
          <div className="mt-4 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 dark:border-gray-500" />
          </div>
        </div>
        <div className={`p-4 border-t ${getLightBorder(isDarkMode)} flex justify-between gap-2`}>
          {wait.simulated && onSimulateTap ? (
            <button
              type="button"
              onClick={onSimulateTap}
              className={`px-3 py-2 rounded-md border text-sm ${getLightBorder(isDarkMode)} ${getPrimaryText(isDarkMode)}`}
              data-testid="reader-simulate-tap"
            >
              Simulate tap (test mode)
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onCancel}
            className={`px-4 py-2 rounded-md border inline-flex items-center gap-2 ${getLightBorder(isDarkMode)} ${getPrimaryText(isDarkMode)}`}
            data-testid="reader-cancel"
          >
            <X size={16} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReaderWaitPanel;
